import { useMemo } from 'react';
import { Scale, ExternalLink, ArrowUpRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { DecisionDetail, ChainLink } from '@hector/shared-types';
import HectorLogo from '@/components/layout/HectorLogo';
import {
  splitDecisionIntoParagraphs,
  isHeadingParagraph,
  formatDecisionTitle,
  formatDecisionCitation,
  formatDecisionDateFr,
} from '@hector/decision-document';

/**
 * Rendu pur d'une décision de justice avec texte intégral structuré.
 *
 * Découpe + typage du `fullText` délégués à `decisionText.ts` (fonctions pures
 * testables) : un `full_text` propre (paragraphes `\n`) comme un « blob » d'un
 * seul tenant produisent le même rendu lisible (cf. incident 2026-06-18).
 * Composant volontairement sans effet de bord : aucun fetch, aucun store, aucun router.
 *
 * Le logo Hector + plume en tête appartient au DOCUMENT de décision (présent ici
 * à l'écran ET dans le PDF téléchargé — `decisionPdfExport.ts`), pas à la chrome
 * du panneau. Le bouton de téléchargement vit dans la chrome (panneau / page).
 *
 * @param decision — objet `DecisionDetail` issu de `@hector/shared-types`
 */
/**
 * Host lisible d'une `source_url` officielle (« courdecassation.fr », « legifrance.gouv.fr »)
 * pour libeller le lien public. Retourne `null` si l'URL est inexploitable — le lien n'est
 * alors pas rendu (jamais de lien cassé).
 */
function publicSourceHost(sourceUrl: string | null): string | null {
  if (!sourceUrl) return null;
  try {
    return new URL(sourceUrl).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

/** En-tête d'un maillon : juridiction (1re ligne du `title` en repli) ; `title` peut être « Cour… \n Chambre ». */
function chainNodeHeading(link: ChainLink): string {
  return link.jurisdiction ?? link.title.split('\n')[0] ?? link.title;
}

/** Ligne méta secondaire d'un maillon : chambre · n° · date (les champs absents sont omis). */
function chainNodeMeta(link: ChainLink): string {
  return [link.chamber, link.number ? `n° ${link.number}` : null, formatDecisionDateFr(link.date)]
    .filter(Boolean)
    .join(' · ');
}

/**
 * Un maillon de chaîne procédurale. Destination par ordre de préférence :
 *  1. `corpusId` + callback fourni → ouvre le viewer interne (décision dans notre corpus) ;
 *  2. `judilibreId` → lien externe vers la source officielle courdecassation.fr ;
 *  3. sinon → texte brut (maillon non résolu, ex. n° de pourvoi seul).
 * Garde la pureté de `DecisionView` : la navigation interne passe par le callback du parent.
 */
function ChainNodeRow({ link, onOpenCorpusDecision }: { link: ChainLink; onOpenCorpusDecision?: (corpusId: string) => void }) {
  const heading = chainNodeHeading(link);
  const meta = chainNodeMeta(link);
  const body = (
    <>
      <div className="flex items-center gap-1.5 text-sm font-medium text-charcoal">
        <Scale className="h-3.5 w-3.5 shrink-0 text-grey-400" />
        <span className="truncate">{heading}</span>
      </div>
      {meta && <div className="mt-0.5 pl-5 text-xs text-grey-500">{meta}</div>}
      {link.solution && <div className="mt-0.5 pl-5 text-xs italic text-grey-400">{link.solution}</div>}
    </>
  );

  if (link.corpusId && onOpenCorpusDecision) {
    const corpusId = link.corpusId;
    return (
      <button
        type="button"
        onClick={() => onOpenCorpusDecision(corpusId)}
        className="block w-full rounded-lg border border-grey-200 bg-white px-3 py-2 text-left transition-colors hover:border-accent-blue/40 hover:bg-accent-blue/[0.03]"
      >
        {body}
      </button>
    );
  }
  if (link.judilibreId) {
    return (
      <a
        href={`https://www.courdecassation.fr/decision/${link.judilibreId}`}
        target="_blank"
        rel="noopener noreferrer"
        className="group block rounded-lg border border-grey-200 bg-white px-3 py-2 transition-colors hover:border-accent-blue/40 hover:bg-accent-blue/[0.03]"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">{body}</div>
          <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-grey-300 group-hover:text-accent-blue" />
        </div>
      </a>
    );
  }
  return <div className="rounded-lg border border-grey-200 bg-white px-3 py-2">{body}</div>;
}

export function DecisionView({
  decision,
  onOpenCorpusDecision,
}: {
  decision: DecisionDetail;
  /** Ouvre une décision de NOTRE corpus dans le viewer (maillon de chaîne deep-linkable). Optionnel :
   *  sans lui, les maillons à `corpusId` retombent sur le lien externe courdecassation.fr. */
  onOpenCorpusDecision?: (corpusId: string) => void;
}) {
  const blocks = useMemo(
    () => splitDecisionIntoParagraphs(decision.fullText),
    [decision.fullText],
  );

  // Titre enrichi : juridiction complète AVEC ville (« Tribunal administratif de
  // Bordeaux »), ligne de citation (date longue · n°), méta secondaire (chambre, ECLI, publication).
  const title = formatDecisionTitle(decision);
  const citation = formatDecisionCitation(decision);
  const subMeta = [decision.chamber, decision.ecli, decision.publication].filter(Boolean) as string[];

  // Lien vers la source officielle — droit privé (judiciaire) uniquement : 100 % des décisions
  // judiciaires portent un `source_url` trusted (courdecassation.fr / legifrance.gouv.fr), alors
  // que l'admin (CE/CAA/TA) n'en a aucun (le viewer interne EST la seule source). L'avocat doit
  // pouvoir ouvrir la décision à sa source officielle.
  const sourceHost = decision.ordre === 'judiciaire' ? publicSourceHost(decision.sourceUrl) : null;

  // Chaîne procédurale (judiciaire cc/ca, peuplée par le backfill). Priorité d'affichage :
  // timeline complète (≥2 maillons, le cas riche) > décision attaquée seule (`contested`, le cas
  // dominant ~91 % CC) > suite/pourvoi (`forward`). L'encadré n'apparaît pas si rien à montrer.
  const { chainNodes, chainTitle } = useMemo<{ chainNodes: ChainLink[]; chainTitle: string }>(() => {
    const chain = decision.proceduralChain;
    if (!chain) return { chainNodes: [], chainTitle: '' };
    if (chain.timeline.length >= 2) return { chainNodes: chain.timeline, chainTitle: 'Chaîne procédurale' };
    if (chain.contested) return { chainNodes: [chain.contested], chainTitle: 'Décision attaquée' };
    if (chain.forward) return { chainNodes: [chain.forward], chainTitle: 'Suite de la procédure' };
    return { chainNodes: [], chainTitle: '' };
  }, [decision.proceduralChain]);

  return (
    <article className="overflow-hidden rounded-xl border border-grey-200 bg-white">
      <header className="flex items-start justify-between gap-4 border-b border-grey-100 px-7 py-5">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-charcoal">
            <Scale className="h-4 w-4 shrink-0 text-grey-400" />
            <h1 className="text-lg font-semibold leading-snug">{title}</h1>
          </div>
          {citation && (
            <div className="mt-1 text-sm font-medium text-grey-600">{citation}</div>
          )}
          {subMeta.length > 0 && (
            <div className="mt-1 text-xs text-grey-400">{subMeta.join(' · ')}</div>
          )}
          {sourceHost && (
            <a
              href={decision.sourceUrl ?? undefined}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2.5 inline-flex items-center gap-1.5 text-sm font-medium text-accent-blue hover:underline"
            >
              <ExternalLink className="h-3.5 w-3.5 shrink-0" />
              Lire la décision sur {sourceHost}
            </a>
          )}
        </div>
        {/* Branding du document de décision (logo + plume) — repris tel quel dans le PDF. */}
        <HectorLogo size="sm" className="shrink-0" />
      </header>

      {chainNodes.length > 0 && (
        <section className="border-b border-grey-100 bg-grey-50/50 px-7 py-4">
          <h2 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-grey-500">{chainTitle}</h2>
          <div className="space-y-1.5">
            {chainNodes.map((link, i) => (
              <ChainNodeRow key={`${link.judilibreId ?? link.number ?? i}`} link={link} onOpenCorpusDecision={onOpenCorpusDecision} />
            ))}
          </div>
        </section>
      )}

      <div className="px-7 py-6 font-serif text-[15.5px] leading-[1.72] text-grey-800">
        {blocks.length === 0 ? (
          <p className="italic text-grey-400">
            Texte intégral indisponible pour cette décision.
          </p>
        ) : (
          blocks.map((b, i) => (
            <p
              key={i}
              className={cn(
                'my-2',
                isHeadingParagraph(b) && 'mt-5 font-sans text-sm font-bold text-charcoal',
              )}
            >
              {b}
            </p>
          ))
        )}
      </div>

      <footer className="border-t border-grey-100 bg-grey-50 px-7 py-2.5 text-[11px] text-grey-400">
        Texte intégral servi depuis le corpus interne Hector.
      </footer>
    </article>
  );
}
